// ─── Premium Button Variants ───
// ButtonCall  — blue glow, confident weight
// ButtonMeeting — gold with dark foreground, "trophy" feel
// ButtonSprint  — violet energy accent, animated edge pulse
// Button       — base with primary / secondary / ghost / outline variants

import { useState, useRef, useCallback } from 'react'
import { motion, type HTMLMotionProps, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils.ts'

// ─── Types ───

export type ButtonVariant =
  | 'call'
  | 'meeting'
  | 'sprint'
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

export interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  children?: React.ReactNode
}

// ─── Style maps ───

const VARIANT_BASE: Record<ButtonVariant, string> = {
  call: [
    'btn-call',
    'border border-metric-call/40 bg-metric-call/10 text-metric-call',
    'hover:bg-metric-call/20',
    'focus-visible:ring-2 focus-visible:ring-metric-call/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  ].join(' '),

  meeting: [
    'btn-meeting',
    'border border-metric-meeting/40 bg-metric-meeting/10 text-metric-meeting',
    'hover:bg-metric-meeting/20',
    'focus-visible:ring-2 focus-visible:ring-metric-meeting/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  ].join(' '),

  sprint: [
    'btn-sprint',
    'border-0 text-white overflow-visible',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  ].join(' '),

  primary: [
    'border border-transparent bg-primary text-primary-foreground',
    'hover:bg-primary/90',
    'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  ].join(' '),

  secondary: [
    'border border-transparent bg-muted text-foreground',
    'hover:bg-muted/70',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  ].join(' '),

  ghost: [
    'border border-transparent text-foreground',
    'hover:bg-accent hover:text-accent-foreground',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  ].join(' '),

  outline: [
    'border border-border bg-transparent text-foreground',
    'hover:bg-accent hover:text-accent-foreground',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  ].join(' '),
}

const SIZE_STYLES: Record<ButtonSize, string> = {
  xs: 'h-6 rounded-md px-2 text-xs gap-1',
  sm: 'h-8 rounded-lg px-3 text-xs gap-1.5',
  md: 'h-9 rounded-lg px-4 text-sm gap-2',
  lg: 'h-11 rounded-xl px-5 text-sm gap-2',
}

/** Spark flash color per variant — shown briefly on press */
const SPARK_COLORS: Record<ButtonVariant, string> = {
  call:      'rgba(96, 165, 250, 0.4)',   // blue-400
  meeting:   'rgba(251, 191, 36, 0.4)',   // amber-400
  sprint:    'rgba(167, 139, 250, 0.35)', // violet-400
  primary:   'rgba(255, 255, 255, 0.2)',
  secondary: 'rgba(255, 255, 255, 0.15)',
  ghost:     'rgba(255, 255, 255, 0.12)',
  outline:   'rgba(255, 255, 255, 0.12)',
}

// ─── Component ───

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  className,
  children,
  style,
  onTap,
  ...props
}: ButtonProps) {
  const reducedMotion = useReducedMotion()
  const sparkIdRef = useRef(0)
  const [activeSpark, setActiveSpark] = useState<number | null>(null)

  const handleTap = useCallback(
    (
      event: Parameters<NonNullable<HTMLMotionProps<'button'>['onTap']>>[0],
      info: Parameters<NonNullable<HTMLMotionProps<'button'>['onTap']>>[1],
    ) => {
      if (!disabled && !loading && !reducedMotion) {
        sparkIdRef.current += 1
        setActiveSpark(sparkIdRef.current)
      }
      onTap?.(event, info)
    },
    [disabled, loading, reducedMotion, onTap],
  )

  return (
    <motion.button
      whileHover={!disabled && !loading ? { scale: 1.02 } : {}}
      whileTap={!disabled && !loading ? { scale: 0.97, filter: 'brightness(1.1)' } : {}}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      disabled={disabled || loading}
      onTap={handleTap}
      className={cn(
        // Base
        'inline-flex items-center justify-center font-medium outline-none transition-colors',
        'relative overflow-hidden',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:outline-none',
        // Loading shimmer overlay
        loading && 'btn-loading',
        // Variant + size
        VARIANT_BASE[variant],
        SIZE_STYLES[size],
        className,
      )}
      style={style}
      {...props}
    >
      {children}

      {/* Spark flash — radial burst on press, respects reduced-motion */}
      {activeSpark !== null && (
        <motion.span
          key={activeSpark}
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: SPARK_COLORS[variant], borderRadius: 'inherit' }}
          initial={{ opacity: 0.75, scale: 0.6 }}
          animate={{ opacity: 0, scale: 2.2 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          onAnimationComplete={() => setActiveSpark(null)}
        />
      )}
    </motion.button>
  )
}

// ─── Convenience exports ───

export function ButtonCall(props: Omit<ButtonProps, 'variant'>) {
  return <Button variant="call" {...props} />
}

export function ButtonMeeting(props: Omit<ButtonProps, 'variant'>) {
  return <Button variant="meeting" {...props} />
}

export function ButtonSprint(props: Omit<ButtonProps, 'variant'>) {
  return <Button variant="sprint" {...props} />
}
